import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';
import type { ComoAssetListPreviewAsset } from './como-asset-list-block.config';

/** Serializable preview VM for `ComoHomeMosaicBlockComponent`. */
export type ComoHomeMosaicBlockScenarioContent = {
  comoUser: {
    points_balance: {
      used_by_payment: boolean;
      monetary: number;
      non_monetary: number;
    };
    credit_balance: {
      used_by_payment: boolean;
      monetary: number;
      non_monetary: number;
    };
    assets: readonly ComoAssetListPreviewAsset[];
  } | null;
};

export const COMOHOMEMOSAICBLOCK_LEGACY_FIELD_ORDER = [
  "display_points",
  "display_credits",
  "display_assets",
  "assets_heading",
  "assets_subheading"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  comoHomeMosaicBlockDescriptor: _generatedComoHomeMosaicBlockDescriptor,
  comoHomeMosaicBlockBlocDefinition,
  comoHomeMosaicBlockDashboardLayout,
  comoHomeMosaicBlockConfig,
  comoHomeMosaicBlockResolveConfig,
  comoHomeMosaicBlockProperties,
  comoHomeMosaicBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Como - Mosaic (points, credits, assets)",
    componentType: 'ComoHomeMosaicBlockComponent',
    category: 'como',
    description: "Como - Mosaic (points, credits, assets).",
    keywords: [],
    sharedFields: {}
  },

  () => ({
    display_points: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.como-home-mosaic.display-points.label",
      }
    }),
    points_card_link: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.como-home-mosaic.points-card-link.label",
        help: "pages.blocks.como-home-mosaic.points-card-link.help",
      }
    }),
    points_background_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: "pages.blocks.como-home-mosaic.points-background-color.label",
      }
    }),
    points_text_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: "pages.blocks.como-home-mosaic.points-text-color.label",
      }
    }),
    display_credits: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.como-home-mosaic.display-credits.label",
      }
    }),
    credits_card_link: propertyValue({
      type: 'link',
      value: '',
      options: {
        label: "pages.blocks.como-home-mosaic.credits-card-link.label",
      }
    }),
    credits_background_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: "pages.blocks.como-home-mosaic.credits-background-color.label",
      }
    }),
    credits_text_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: "pages.blocks.como-home-mosaic.credits-text-color.label",
      }
    }),
    display_assets: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.como-home-mosaic.display-assets.label",
      }
    }),
    assets_heading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.como-home-mosaic.assets-heading.label",
      }
    }),
    assets_subheading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.como-home-mosaic.assets-subheading.label",
      }
    })
  }),

  () => ({
    // Links/colors for points & credits cards are not wired in the webapp template.
    omitFields: [
      "points_card_link",
      "points_background_color",
      "points_text_color",
      "credits_card_link",
      "credits_background_color",
      "credits_text_color",
    ] as const,
    tabs: [
      {
        label: "pages.blocks.como-home-mosaic.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.como-home-mosaic.fieldset.assets.label",
            fields: [
              "display_assets",
              {
                prop: "assets_heading",
                visibleWhen: { display_assets: true },
              },
              {
                prop: "assets_subheading",
                visibleWhen: { display_assets: true },
              },
              {
                prop: "display_points",
                visibleWhen: { display_assets: true },
              },
              {
                prop: "display_credits",
                visibleWhen: { display_assets: true },
              },
            ],
          },
        ],
      },
    ],
  })
);

export function comoHomeMosaicBlockDescriptor(): IBlockDescriptor;
export function comoHomeMosaicBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function comoHomeMosaicBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedComoHomeMosaicBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedComoHomeMosaicBlockDescriptor, comoHomeMosaicBlockProperties, fieldOrder);
}

export {
  comoHomeMosaicBlockBlocDefinition,
  comoHomeMosaicBlockDashboardLayout,
  comoHomeMosaicBlockProperties,
  comoHomeMosaicBlockConfig,
  comoHomeMosaicBlockResolveConfig,
  comoHomeMosaicBlockDefinitionSetupScenarios,
};

export type ComoHomeMosaicBlockConfig = typeof comoHomeMosaicBlockConfig;
